import React from 'react';
import { Box, Container, Grid, List, ListItem } from '@mui/material';
import { LocalizedButton as Button, LocalizedListItemText as ListItemText } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';
import KundlistarSchema from '../components/KundlistarSchema';
import KundlistarFAQSchema from '../components/KundlistarFAQSchema';
import { BRAND } from '../config/brand';

const features = [
  'Vedic Kundli chart generation',
  'KP astrology and cuspal analysis',
  'Nadi astrology and BNN analysis',
  'Jaimini astrology',
  'Lal Kitaab',
  'Tajika Varshphal',
  'Muhurat and daily planning',
  'Personalised Gochar and transit insights',
  'Numerology and Astro Vastu',
  'Premium AI-powered astrology reports',
];

const KundlistarPage: React.FC = () => (
  <>
    <SEO
      title="What is Kundlistar? Advanced Astrology Software for KP, Nadi & Vedic"
      description="Kundlistar is an astrology software and app for Vedic, KP, Nadi, BNN, Jaimini, Lal Kitaab, Varshphal, Muhurat and personalised Gochar analysis."
      path="/kundlistar-studio"
      keywords="Kundlistar, what is Kundlistar, Kundlistar astrology software, Kundlistar app, Kundlistar KP astrology, Kundlistar Nadi astrology"
    />
    <KundlistarSchema />
    <KundlistarFAQSchema />

    <Box sx={{ bgcolor: "var(--app-surface-neutral, #f7fbf8)", py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={5} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="overline" color="primary" sx={{ fontWeight: 600 }}>
              Kundlistar Astrology Software
            </Typography>
            <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
              What is Kundlistar?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
              Kundlistar is an advanced astrology software platform created for astrologers,
              astrology students and serious learners. It helps users generate and study Vedic
              charts, KP astrology, Nadi astrology, BNN, Jaimini, Lal Kitaab, Tajika Varshphal,
              Muhurat and personalised Gochar insights.
            </Typography>
            <Button component={RouterLink} to="/calculate" variant="contained" size="large">
              Explore Kundlistar
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ p: 3, borderRadius: 3, bgcolor: "var(--app-surface-neutral, white)", border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
                Kundlistar astrology features
              </Typography>
              <List dense>
                {features.map((feature) => (
                  <ListItem key={feature} disableGutters>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Container maxWidth="md" sx={{ py: 7 }}>
      <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
        Why people search for Kundlistar
      </Typography>
      <Typography paragraph>
        Kundlistar is available on web and Android. Users can access the same account on mobile
        and web, making it useful for astrologers who need astrology calculations, chart analysis
        and premium AI-powered reports.
      </Typography>
      <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
        Use Kundlistar on mobile and web
      </Typography>
      <Typography paragraph>
        Use Kundlistar at {BRAND.domain} or download the Android app from Google Play. The platform
        is built for practical astrology workflows, from chart generation to premium reports.
      </Typography>
    </Container>
  </>
);

export default KundlistarPage;
